import React from "react";

import {
	Navigate,
	RouteObject,
	RouterProvider,
	createBrowserRouter,
	matchPath,
	useLocation,
} from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";
import { isFeatureOn } from "@/hooks/useFeatureFlag";

import { AuthLayout } from "@/components/templates";
import { DashboardLayout } from "@/components/templates";
import { ErrorLayout } from "@/components/templates";

import { returnToOf, takeReturnTo } from "@/utils/UReturnTo";

import { TRoute } from "@/types";

import { OnboardingGate } from "./OnboardingGate";
import { PublicRoutes, PrivateRoutes } from "./Routes";

const pathsOf = (route: TRoute): string[] =>
	typeof route.path === "string" ? [route.path] : (route.path ?? []);

/**
 * Whether `tab` is the route of `pathname`: an exact match, or a
 * non-root entry of the tab that `pathname` starts with ("/calendar"
 * matches "/calendar/5"; "/" only matches itself).
 */
export const isRouteActiveAt = (tab: TRoute, pathname: string): boolean =>
	pathsOf(tab).some(
		(p) =>
			p === pathname ||
			(p !== "/" && !!matchPath({ path: p, end: false }, pathname))
	);

/** Hook form: follows the router's location. */
export const useIsRouteActive = () => {
	const { pathname } = useLocation();
	return (tab: TRoute) => isRouteActiveAt(tab, pathname);
};

const PrivateRoute: React.FC<{ route: TRoute }> = ({ route }) => {
	const { isAuthenticated, hasRole, user } = useAuth();
	const location = useLocation();

	// `from`: where to come back to once signed in (Auth reads it).
	if (!isAuthenticated)
		return <Navigate to="/login" replace state={{ from: location }} />;
	if (route.roles && !hasRole(route.roles, route.needAllRoles))
		return <Navigate to="/" replace />;
	// A paused feature's page does not exist for them (the API 404s it too).
	if (route.feature && !isFeatureOn(user, route.feature))
		return <Navigate to="/" replace />;

	const Page = route.element!;
	const page = <Page />;

	// No calendar yet: the onboarding, whatever the page asked for.
	if (route.layout) {
		const Layout = route.layout;
		return (
			<OnboardingGate>
				<Layout>{page}</Layout>
			</OnboardingGate>
		);
	}

	return (
		<OnboardingGate>
			<DashboardLayout {...route}>{page}</DashboardLayout>
		</OnboardingGate>
	);
};

/**
 * The signed-in user has nothing to do on /login or /register: send them
 * on - to the page a signed-out visit was after, else home. Safe from a
 * flash because AuthProvider renders nothing until the boot-time refresh
 * has settled, so isAuthenticated is final here. The destination is read
 * once (a ref): the remembered page is consumed on read.
 */
export const GuestRoute: React.FC<React.PropsWithChildren> = ({ children }) => {
	const { isAuthenticated } = useAuth();
	const location = useLocation();
	const destination = React.useRef<string | undefined>(undefined);
	if (isAuthenticated) {
		destination.current ??= returnToOf(location) ?? takeReturnTo();
		return <Navigate to={destination.current} replace />;
	}
	return <>{children}</>;
};

const publicRouteObjects = (route: TRoute): RouteObject[] => {
	if (!route.path || route.hidden) return [];
	const Page = route.element ?? ErrorLayout;
	const page = (
		<AuthLayout>
			<Page />
		</AuthLayout>
	);
	const element = route.guestOnly ? <GuestRoute>{page}</GuestRoute> : page;
	return pathsOf(route).map((path) => ({ path, element }));
};

const privateRouteObjects = (route: TRoute): RouteObject[] => {
	// A menu entry without an element (the nav bar's "Home", "views")
	// points at a page another route renders: nothing to mount.
	if (!route.path || route.hidden || !route.element) return [];
	const element = <PrivateRoute route={route} />;
	return pathsOf(route).map((path) => ({
		path,
		element,
		// A render error in a page shows the error layout in place of a
		// blank screen; the 404 route stays the "*" entry of PrivateRoutes.
		errorElement: <ErrorLayout />,
	}));
};

/** The route table, as react-router's data router takes it. */
export const routeObjects = (): RouteObject[] => [
	...PublicRoutes.flatMap(publicRouteObjects),
	...PrivateRoutes.flatMap(privateRouteObjects),
];

const router = createBrowserRouter(routeObjects());

const Router: React.FC = () => <RouterProvider router={router} />;

export default Router;
